import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Link,
  Divider,
  Image,
  Typography,
  Flex,
  VStack,
} from '@tq/ui-components';
import { tqc } from '@tq/ui-components/dist/system';
import { transparentize } from '@tq/ui-components/dist/theme-tools';
import { FaLinkedinIn, FaFacebookF, FaTwitter } from 'react-icons/fa';

import tqLogo from 'assets/tq-logo.svg';
import { Copyright } from './Copyright';
import { CUSTOM_TQ_COLOURS } from 'styles';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { useIsMobile } from '@tq/ui-components/dist/hooks';

export const TqAppFooter: React.FC = () => {
  const { t } = useTranslation('footer');
  const isMobile = useIsMobile();

  const footerMenu = [
    t('development_management_practice_notes', 'Development management practice notes'),
    t('accessibility_statement', 'Accessibility statement'),
    t('cookie_notice', 'Cookie notice'),
    t('terms_and_conditions', 'Website terms and conditions'),
    t('help', 'Help'),
    t('contact_us', 'Contact us'),
  ];

  return (
    <tqc.div as='footer' className='tqc-footer'>
      <Box pt={'3rem'} backgroundColor='#072C4D'>
        <Container>
          <Flex
            flexWrap={{ base: 'wrap', lg: 'nowrap' }}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Image
              src={tqLogo}
              w={'13.8rem'}
              h={'3.75rem'}
              alt={t('logo_alt', 'Consultee hub logo')}
            />
            <Grid gridAutoFlow={'column'} gridGap={'1rem'} alignItems='center'>
              <FaLinkedinIn className='footer-fa-icon fa-xs' />
              <FaFacebookF className='footer-fa-icon' />
              <FaTwitter className='footer-fa-icon' />
            </Grid>
          </Flex>
          <Divider
            sx={{
              borderColor: transparentize('grey.400', isMobile ? 0.8 : 0.2),
              mb: '2rem',
              mt: '2.5rem',
            }}
          />
          <>
            <Typography
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.013em',
                fontSize: '1rem',
                lineHeight: '1.5rem',
                color: 'white',
                mb: '0.75rem',
              }}
            >
              {t('useful_links', 'Useful links')}
            </Typography>
            <tqc.div
              mb={3}
              display={'flex'}
              flexWrap={'wrap'}
              alignItems='center'
              maxWidth={{ lg: '60%' }}
              gridColumnGap={'3.75rem'}
              sx={{
                '.footerMenu:nth-child(4)': {
                  minWidth: '18.56rem',
                },
                '.footerMenu:nth-child(5)': {
                  minWidth: '10.3rem',
                },
              }}
            >
              {footerMenu.map((subMenu, id) => {
                return (
                  <VStack key={id} alignItems='left' className='footerMenu'>
                    <Link
                      key={id}
                      sx={{
                        mb: '0.75rem',
                        color: 'white',
                        fontSize: '1rem',
                        letterSpacing: 0,
                        border: '1px solid',
                        fontWeight: 'normal',
                        textDecoration: 'none',
                        display: 'inline-block',
                        borderColor: 'transparent',
                        textUnderlineOffset: '5px',
                        width: 'fit-content',
                        _hover: {
                          color: 'white',
                          textDecoration: 'underline',
                        },
                        '&:focus, &:active': {
                          color: CUSTOM_TQ_COLOURS.blue275,
                          textDecoration: 'underline',
                          backgroundColor: `${COLOURS.blue[200]}`,
                        },
                      }}
                    >
                      {subMenu}
                    </Link>
                  </VStack>
                );
              })}
            </tqc.div>
          </>
          <Divider
            sx={{
              borderColor: transparentize('grey.400', isMobile ? 0.8 : 0.2),
              mt: '2rem',
            }}
          />
        </Container>
      </Box>

      <Copyright />
    </tqc.div>
  );
};
